/**
 * Outlier Scatter Renderer
 * 
 * Renders scatter plot with outlier detection and highlighting.
 * Shows data points with outliers marked differently.
 */

import type { VisualizationRenderer } from '../registry.js';
import { designTokens, escapeHtml } from '../../../core/design-system.js';

export const renderOutlierScatter: VisualizationRenderer = (data, meta) => {
  // Expected data format: array of { x: number, y: number, label?: string, isOutlier?: boolean }
  // Or: { points: Array<{ x, y, label?, isOutlier? }>, outliers?: number[] }
  if (!Array.isArray(data) || data.length === 0) {
    return renderEmptyState('No data to display');
  }

  // Normalize data format
  const points = data.map(item => ({
    x: Number(item.x) || 0,
    y: Number(item.y) || 0,
    label: item.label || item.name || '',
    isOutlier: item.isOutlier !== undefined ? Boolean(item.isOutlier) : false
  }));

  // Calculate bounds
  const xValues = points.map(p => p.x);
  const yValues = points.map(p => p.y);
  const minX = Math.min(...xValues);
  const maxX = Math.max(...xValues);
  const minY = Math.min(...yValues);
  const maxY = Math.max(...yValues);
  
  const xRange = maxX - minX || 1;
  const yRange = maxY - minY || 1;

  // Chart dimensions
  const width = 600;
  const height = 400;
  const padding = { top: 20, right: 100, bottom: 60, left: 60 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Scale functions
  const scaleX = (x: number) => padding.left + ((x - minX) / xRange) * chartWidth;
  const scaleY = (y: number) => padding.top + chartHeight - ((y - minY) / yRange) * chartHeight;

  // Generate axis lines
  const axisLines = `
    <line
      x1="${padding.left}"
      y1="${padding.top}"
      x2="${padding.left}"
      y2="${padding.top + chartHeight}"
      stroke="${designTokens.contentColor}"
      stroke-width="2"
    />
    <line
      x1="${padding.left}"
      y1="${padding.top + chartHeight}"
      x2="${padding.left + chartWidth}"
      y2="${padding.top + chartHeight}"
      stroke="${designTokens.contentColor}"
      stroke-width="2"
    />
  `;

  // Generate grid lines
  const gridLines = 5;
  const xGridLines = Array.from({ length: gridLines + 1 }, (_, i) => {
    const x = padding.left + (chartWidth / gridLines) * i;
    const value = minX + (xRange / gridLines) * i;
    return `
      <line
        x1="${x}"
        y1="${padding.top}"
        x2="${x}"
        y2="${padding.top + chartHeight}"
        stroke="${designTokens.borderColor}"
        stroke-width="0.5"
        opacity="0.3"
      />
      <text
        x="${x}"
        y="${height - padding.bottom + 15}"
        text-anchor="middle"
        font-size="${designTokens.fontSize.xs}"
        fill="${designTokens.contentColorMuted}"
      >${value.toFixed(1)}</text>
    `;
  }).join('');

  const yGridLines = Array.from({ length: gridLines + 1 }, (_, i) => {
    const y = padding.top + (chartHeight / gridLines) * (gridLines - i);
    const value = minY + (yRange / gridLines) * i;
    return `
      <line
        x1="${padding.left}"
        y1="${y}"
        x2="${padding.left + chartWidth}"
        y2="${y}"
        stroke="${designTokens.borderColor}"
        stroke-width="0.5"
        opacity="0.3"
      />
      <text
        x="${padding.left - 10}"
        y="${y + 4}"
        text-anchor="end"
        font-size="${designTokens.fontSize.xs}"
        fill="${designTokens.contentColorMuted}"
      >${value.toFixed(1)}</text>
    `;
  }).join('');

  // Generate axis labels
  const axisLabels = `
    <text
      x="${width / 2}"
      y="${height - 10}"
      text-anchor="middle"
      font-size="${designTokens.fontSize.sm}"
      font-weight="600"
      fill="${designTokens.contentColor}"
    >${escapeHtml(meta?.tableConfig?.filters?.[0]?.column || 'X Axis')}</text>
    <text
      x="10"
      y="${height / 2}"
      text-anchor="middle"
      font-size="${designTokens.fontSize.sm}"
      font-weight="600"
      fill="${designTokens.contentColor}"
      transform="rotate(-90 10 ${height / 2})"
    >${escapeHtml(meta?.tableConfig?.filters?.[1]?.column || 'Y Axis')}</text>
  `;

  // Generate scatter points
  const scatterPoints = points.map((point, index) => {
    const x = scaleX(point.x);
    const y = scaleY(point.y);
    const isOutlier = point.isOutlier;
    const tooltipId = `outlier-tooltip-${Date.now()}-${index}`;
    
    const pointColor = isOutlier 
      ? 'rgba(255, 100, 100, 0.8)' 
      : 'rgba(102, 204, 255, 0.6)';
    const pointStroke = isOutlier 
      ? 'rgba(255, 100, 100, 1)' 
      : 'rgba(102, 204, 255, 0.9)';
    const pointRadius = isOutlier ? 6 : 4;
    
    // Calculate Y position for coordinates text (depends on whether label exists)
    const coordsY = point.label ? y + 2 : y - 12;

    return `
      <g>
        <circle
          cx="${x}"
          cy="${y}"
          r="${pointRadius}"
          fill="${pointColor}"
          stroke="${pointStroke}"
          stroke-width="${isOutlier ? '2' : '1'}"
          style="cursor: pointer; transition: r 0.2s;"
          data-label="${escapeHtml(point.label)}"
          data-x="${point.x}"
          data-y="${point.y}"
          data-outlier="${isOutlier}"
          onmouseenter="
            this.setAttribute('r', '${pointRadius + 2}');
            const tooltip = document.getElementById('${tooltipId}');
            if (tooltip) tooltip.style.display = 'block';
          "
          onmouseleave="
            this.setAttribute('r', '${pointRadius}');
            const tooltip = document.getElementById('${tooltipId}');
            if (tooltip) tooltip.style.display = 'none';
          "
        />
        <g id="${tooltipId}" style="display: none; pointer-events: none;">
          <rect
            x="${x + 10}"
            y="${y - 30}"
            width="120"
            height="${point.label ? '50' : '35'}"
            fill="rgba(30, 32, 40, 0.95)"
            stroke="${designTokens.borderColor}"
            stroke-width="1"
            rx="4"
          />
          ${point.label ? `
            <text
              x="${x + 15}"
              y="${y - 12}"
              font-size="${designTokens.fontSize.xs}"
              font-weight="600"
              fill="${designTokens.contentColor}"
            >${escapeHtml(point.label)}</text>
          ` : ''}
          <text
            x="${x + 15}"
            y="${coordsY}"
            font-size="${designTokens.fontSize.xs}"
            fill="${designTokens.contentColorMuted}"
          >X: ${point.x.toFixed(2)}, Y: ${point.y.toFixed(2)}</text>
          ${isOutlier ? `
            <text
              x="${x + 15}"
              y="${y + 16}"
              font-size="${designTokens.fontSize.xs}"
              fill="rgba(255, 100, 100, 0.9)"
              font-weight="600"
            >⚠ Outlier</text>
          ` : ''}
        </g>
      </g>
    `;
  }).join('');

  // Generate legend
  const legend = `
    <g>
      <circle
        cx="${width - 80}"
        cy="${padding.top + 20}"
        r="4"
        fill="rgba(102, 204, 255, 0.6)"
        stroke="rgba(102, 204, 255, 0.9)"
        stroke-width="1"
      />
      <text
        x="${width - 70}"
        y="${padding.top + 24}"
        font-size="${designTokens.fontSize.xs}"
        fill="${designTokens.contentColor}"
      >Normal</text>
      <circle
        cx="${width - 80}"
        cy="${padding.top + 40}"
        r="6"
        fill="rgba(255, 100, 100, 0.8)"
        stroke="rgba(255, 100, 100, 1)"
        stroke-width="2"
      />
      <text
        x="${width - 70}"
        y="${padding.top + 44}"
        font-size="${designTokens.fontSize.xs}"
        fill="${designTokens.contentColor}"
      >Outlier</text>
    </g>
  `;

  const title = meta?.title || 'Outlier Scatter Plot';
  const outlierCount = points.filter(p => p.isOutlier).length;

  return `
    <div style="width: 100%;">
      ${title ? `
        <div style="
          margin-bottom: ${designTokens.padding.md};
          font-size: ${designTokens.fontSize.md};
          font-weight: 600;
          color: ${designTokens.contentColor};
        ">
          ${escapeHtml(title)}
          ${outlierCount > 0 ? `
            <span style="
              margin-left: ${designTokens.padding.sm};
              font-size: ${designTokens.fontSize.sm};
              color: rgba(255, 100, 100, 0.9);
            ">(${outlierCount} outlier${outlierCount !== 1 ? 's' : ''})</span>
          ` : ''}
        </div>
      ` : ''}
      <div style="
        width: 100%;
        overflow-x: auto;
        overflow-y: hidden;
      ">
        <svg
          width="${width}"
          height="${height}"
          style="
            display: block;
            max-width: 100%;
            height: auto;
          "
        >
          ${xGridLines}
          ${yGridLines}
          ${axisLines}
          ${axisLabels}
          ${scatterPoints}
          ${legend}
        </svg>
      </div>
    </div>
  `;
};

function renderEmptyState(message: string): string {
  return `
    <div style="
      padding: ${designTokens.padding.xl};
      text-align: center;
      color: ${designTokens.contentColorMuted};
      font-size: ${designTokens.fontSize.sm};
    ">${escapeHtml(message)}</div>
  `;
}

